import { useEffect, useRef } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { getFlowPage, loadFlowPage } from "../flow-loader";

/**
 * Lazy shell for the /how-it-works chunk.
 *
 * A dynamic JSX tag (`<Page />` from state) bails the compiler out to the
 * compat renderer, which leanBoot must never ship. Instead we mount the
 * loaded compiled component imperatively through the lean createRoot -
 * the same `Component(props) -> Node` call the app root itself uses.
 *
 * Deep links (TASK-SSR-002): the prerender puts the page content inside
 * this host; data-jethydrate="defer" makes the outer claim pass leave that
 * subtree in place, and the nested hydrateRoot below ADOPTS it (main.tsx
 * preloads the chunk before mounting, so the component is cached by the
 * time this effect runs). SPA navigations find an empty host and mount
 * fresh via createRoot, as before.
 */
export default function FlowPage() {
  const host = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let dead = false;
    let root: any = null;
    const mount = (page: any) => {
      if (dead || !host.current) return;
      if (host.current.hasChildNodes()) {
        root = hydrateRoot(host.current, page);
      } else {
        root = createRoot(host.current);
        root.render(page);
      }
    };
    const cached = getFlowPage();
    if (cached) mount(cached);
    else loadFlowPage().then(mount);
    return () => {
      dead = true;
      if (root) root.unmount();
    };
  }, []);

  // Plain unclassed host: App supplies div.page--flow around it (flow.css
  // has no structural child selectors, so the extra div is layout-neutral).
  return <div ref={host} data-jethydrate="defer" />;
}
